import React, { useState } from "react";
import { useForm } from "react-hook-form";
import { Alert, Box, Dialog, Link } from "@mui/material";
import Login from "../../types/login";
import LoginHeader from "../base/LoginHeader";
import { useNavigate } from "react-router-dom";
import store, { useDispatch } from "../../store";
import {
  associateWebAuthnCredential,
  confirmSignIn,
  fetchAuthSession,
  signIn,
  signInWithRedirect,
} from "aws-amplify/auth";
import {
  Button,
  Flex,
  VisuallyHidden,
  TextField,
  Text,
} from "@aws-amplify/ui-react";
import extractEmailDomain from "../../helpers/extractEmailDomain";
import { checkDomain } from "../../slices/provider";
import { fetchAwsUser } from "../../slices/auth";
// import { setAuthMethod } from "../../helpers/authMethodStore";
import theme from "../../theme";
import { MarkEmailRead } from "@mui/icons-material";

const EmailFirstLogin: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [passkeyFailed, setPasskeyFailed] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [emailOtpState, setEmailOtpState] = useState<
    "needed" | "verifying" | "sending" | "error" | "idle"
  >("idle");

  const { handleSubmit, register } = useForm<Login>({
    mode: "onChange",
    defaultValues: { email: "" },
  });

  const handleEmailSubmit = async (data: Login) => {
    sessionStorage.removeItem("logoutUrl");
    sessionStorage.removeItem("idToken");

    const email = data.email.toLowerCase();
    const domain = extractEmailDomain(email);

    setIsLoading(true);
    setEmail(email);
    try {
      if (domain) {
        await dispatch(checkDomain(domain));
        const state = store.getState();
        const idp =
          state.provider.ssoStatus?.useSso &&
          state.provider.ssoStatus?.domainName;

        if (idp) {
          await signInWithRedirect({ provider: { custom: idp } });
          return await handleSignedIn();
        }
      }

      try {
        const { nextStep } = await signIn({
          username: email,
          options: {
            authFlowType: "USER_AUTH",
            preferredChallenge: "WEB_AUTHN",
          },
        });
        if (nextStep.signInStep === "DONE") return await handleSignedIn();
      } catch {
        setPasskeyFailed(true);
      }

      const { nextStep } = await signIn({
        username: email,
        options: {
          authFlowType: "USER_AUTH",
          preferredChallenge: "EMAIL_OTP",
        },
      });

      if (nextStep.signInStep === "CONFIRM_SIGN_IN_WITH_EMAIL_CODE") {
        setEmailOtpState("needed");
        return;
      }

      throw new Error("Could not sign in!");
    } finally {
      setIsLoading(false);
    }
  };

  const confirmEmailCode = async () => {
    try {
      setEmailOtpState("verifying");
      const r = await confirmSignIn({ challengeResponse: code.trim() });

      if (r.nextStep?.signInStep && r.nextStep.signInStep !== "DONE") {
        setEmailOtpState("error");
        return;
      }

      try {
        await associateWebAuthnCredential();
      } catch (error) {}
      await handleSignedIn();
      setEmailOtpState("idle");
    } catch (e: any) {
      setEmailOtpState("error");
    }
  };

  const handleSignedIn = async () => {
    try {
      await fetchAuthSession(); // refresh token
      await dispatch(fetchAwsUser()); // get user
      navigate("/", { replace: true });
    } catch (error) {}
  };

  return (
    <Box display="flex" justifyContent="center">
      <Box
        justifySelf="center"
        boxSizing="border-box"
        display="block"
        maxWidth={"100%"}
      >
        <LoginHeader />
        <Box
          paddingBottom={3}
          sx={{
            backgroundColor: "white",
            boxShadow: "rgba(13, 26, 38, 0.15) 0px 2px 6px 0px",
            border: "solid 1px rgb(137, 148, 159)",
          }}
        >
          <Box boxSizing="border-box">
            <Box padding={"32px"}>
              <form onSubmit={handleSubmit(handleEmailSubmit)}>
                <Flex direction="column">
                  <Flex direction="column" marginBottom={1}>
                    <VisuallyHidden>Sign in</VisuallyHidden>
                    <TextField
                      label="Email"
                      placeholder="Enter Email"
                      errorMessage="Required"
                      type="email"
                      {...register("email")}
                    />
                  </Flex>
                  <Button
                    variation="primary"
                    type="submit"
                    isLoading={isLoading}
                  >
                    Sign in
                  </Button>
                </Flex>
              </form>
            </Box>
          </Box>
        </Box>
      </Box>
      <Dialog
        open={emailOtpState !== "idle"}
        onClose={() => {
          return;
        }}
        fullWidth
        maxWidth="xs"
      >
        <Flex
          as="form"
          textAlign={"center"}
          direction="column"
          padding={15}
          gap={10}
          onSubmit={async (e) => {
            e.preventDefault();
            await confirmEmailCode();
          }}
        >
          <Text fontSize="50px">
            <MarkEmailRead fontSize="inherit" color="primary" />
          </Text>
          <Text
            variation="primary"
            color={theme.palette.text.primary}
            fontSize="1.5em"
          >
            Check Your Email
          </Text>
          <Text variation="primary" color={theme.palette.text.primary}>
            To sign in, please enter the code we sent to:
          </Text>
          <Text variation="primary" fontWeight={600}>
            {email ?? "your email"}
          </Text>
          {passkeyFailed && (
            <Alert
              severity="info"
              sx={{
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <Link
                component="button"
                onClick={() => setEmailOtpState("idle")}
                sx={{ p: 0, m: 0, fontWeight: 600 }}
              >
                Retry passkey
              </Link>{" "}
              or enter OTP from email.
            </Alert>
          )}
          <Flex direction="column" marginTop={10}>
            <VisuallyHidden>Validate</VisuallyHidden>
            <TextField
              id="code"
              value={code}
              label="Verification Code"
              labelHidden
              placeholder="Enter Code from Email"
              errorMessage="Required"
              autoFocus
              inputMode="numeric"
              autoComplete="one-time-code"
              isRequired
              onChange={(e) => setCode(e.target.value)}
            />
          </Flex>
          <Button
            variation="primary"
            backgroundColor={theme.palette.primary.main}
            type="submit"
            isLoading={emailOtpState === "verifying"}
            disabled={emailOtpState === "sending"}
          >
            Validate
          </Button>
          {emailOtpState === "error" && (
            <Alert
              severity="error"
              sx={{
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              That code didn’t work. Please try again.
            </Alert>
          )}
          <Button
            variation="link"
            isLoading={emailOtpState === "sending"}
            disabled={emailOtpState === "verifying"}
            onClick={async () => {
              try {
                setEmailOtpState("sending");
                await signIn({
                  username: email,
                  options: {
                    authFlowType: "USER_AUTH",
                    preferredChallenge: "EMAIL_OTP",
                  },
                });
                setEmailOtpState("needed");
              } catch {
                setEmailOtpState("error");
              }
            }}
            color={theme.palette.error.main}
            size="small"
          >
            Resend Code
          </Button>
        </Flex>
      </Dialog>
    </Box>
  );
};

export default EmailFirstLogin;
